import Layout from '@src/components/layout/Layout';

import {
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';

import OrganisationImageFallback from '@src/components/organisation/OrganisationImageFallback';

import theme from '@src/theme';
import { useSnackbar } from 'notistack';
import OrganisationForm, {
  OrganisationFormValues,
} from '../organisations-add/components/OrganisationForm';
import useGetOrganisationLazy from '@src/adapters/hooks/organisation/useGetOrganisationLazy';
import useUpdateOrganisationLazy from '@src/adapters/hooks/organisation/useUpdateOrganisationLazy';
import { useGetOrganisationUploadUrlLazy } from '@src/adapters/hooks/organisation/useGetOrganisationUploadUrl';
import { useNavigationStore } from '@src/adapters/stores/useNavigationStore';

const PREFIX = 'OrganisationEdit';
const FORM_ID = 'organisation-edit-form';

const classes = {
  pageTitle: `${PREFIX}-pageTitle`,
  field: `${PREFIX}-field`,
};

const StyledLayout = styled(Layout)(({ theme }) => ({
  [`& .${classes.pageTitle}`]: {
    marginBottom: '33px',
  },
  [`& .${classes.field}`]: {
    width: '438px',

    '& .MuiInputBase-root': {
      height: '56px',
    },

    '& .MuiInputBase-input': {
      color: theme.palette.text.secondary,
    },
  },
}));

function OrganisationEdit() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { currentOrganisation } = useNavigationStore();
  const { execute: getOrganisation } = useGetOrganisationLazy();
  const { execute: updateOrganisation } = useUpdateOrganisationLazy();
  const { execute: getUploadUrl } = useGetOrganisationUploadUrlLazy();

  const {
    reset,
    register,
    handleSubmit,
    formState: { isDirty },
  } = useForm<OrganisationFormValues>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [image, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [editMenu, showEditMenu] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const response = await getOrganisation({
        id: currentOrganisation?.id ?? '',
      });

      reset({
        shortCode: response.shortCode,
        name: response.name,
        codeSet: response.codeSet,
      });

      if (response.logoUrl) setImagePreview(response.logoUrl);
    })();
  }, [enqueueSnackbar, getOrganisation, currentOrganisation?.id, reset]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const onSubmit = async (data: any) => {
    if (!isDirty) {
      navigate(-1);
      return;
    }
    const result = await updateOrganisation({
      id: currentOrganisation?.id,
      name: data.name,
      codeSet: data.codeSet,
      logoImageType: image?.type,
    });
    if (!result) {
      enqueueSnackbar('Failed to update organisation', { variant: 'error' });
      return;
    }

    if (image) {
      const urlRes = await getUploadUrl({
        id: result.id!,
        imageType: image.type,
      });
      const uploadRes = await fetch(urlRes.url, {
        method: 'PUT',
        headers: { ...urlRes.headers, 'Content-Type': image.type },
        body: image,
      });

      if (!uploadRes.ok) {
        enqueueSnackbar('Logo upload failed', { variant: 'error' });
      }
    }
    enqueueSnackbar('Organisation Updated', { variant: 'success' });
    navigate(-2);
  };

  const handleReplaceLogo = () => {
    showEditMenu(false);
    fileInputRef.current?.click();
  };

  const handleRemoveLogo = () => {
    setImageFile(null);
    setImagePreview(null);
    showEditMenu(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePickImage = () => {
    if (!image && !imagePreview) {
      fileInputRef.current?.click();
    } else {
      showEditMenu((prev) => !prev);
    }
  };

  return (
    <StyledLayout>
      <Box sx={{ margin: 'auto', width: 949 }}>
        <Stack
          className={classes.pageTitle}
          direction={'row'}
          justifyContent="space-between">
          <Typography variant="h4" color={theme.palette.text.secondary}>
            Edit organisation
          </Typography>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
            }}>
            <Button
              onClick={() => navigate(-1)}
              variant="outlined"
              size="large"
              color="primary">
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              variant="contained"
              size="large"
              color="primary">
              Finish
            </Button>
          </Box>
        </Stack>

        <Paper>
          <Stack direction="row" alignItems="center" gap={2} p={3}>
            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
              <OrganisationImageFallback
                src={imagePreview}
                alt={'Organisation Image'}
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
              <EditIcon
                onClick={handlePickImage}
                sx={{
                  color: '#9DA5BA',
                  cursor: 'pointer',
                  alignSelf: 'center',
                  ml: '5px',
                }}
              />

              {editMenu && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: '27%',
                    left: '88%',
                    bgcolor: '#fff',
                    boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
                    borderRadius: 2,
                    zIndex: 10,
                    minWidth: 140,
                  }}>
                  <Box
                    onClick={handleReplaceLogo}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      fontSize: 14,
                      '&:hover': { bgcolor: '#F5F5F5' },
                    }}>
                    Replace logo
                  </Box>
                  <Box
                    onClick={handleRemoveLogo}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      fontSize: 14,
                      '&:hover': { bgcolor: '#F5F5F5' },
                    }}>
                    Remove logo
                  </Box>
                </Box>
              )}
            </Box>
            <Typography variant="h6" color={theme.palette.text.secondary}>
              Organisation details
            </Typography>
          </Stack>

          <Divider />
          <OrganisationForm
            formId={FORM_ID}
            register={register}
            onSubmit={handleSubmit(onSubmit)}
            fieldClassName={classes.field}
            disableOrganisationId
          />
        </Paper>
      </Box>
    </StyledLayout>
  );
}

export default OrganisationEdit;
